import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, EyeSlash, CheckCircle, XCircle, Sparkle } from "@phosphor-icons/react";
import { LogoWordmark } from "@/components/Logo";
import { api } from "@/lib/api";

// Política de contraseñas (replicada en cliente para feedback en vivo).
// El backend valida igualmente con `password_policy.assert_password`.
const RULES = [
  { id: "len",     label: "Al menos 10 caracteres",                       test: (p) => p.length >= 10 },
  { id: "space",   label: "Es una frase (incluye al menos un espacio)",    test: (p) => /\s/.test(p) },
  { id: "upper",   label: "Al menos una mayúscula",                        test: (p) => /[A-ZÁÉÍÓÚÜÑ]/.test(p) },
  { id: "digits2", label: "Al menos un número de 2 cifras o más",          test: (p) => /\d{2,}/.test(p) },
];


export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const checks = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(pwd) })), [pwd]);
  const allOk = checks.every((c) => c.ok);
  const matches = pwd.length > 0 && pwd === pwd2;
  const canSubmit = allOk && matches && !!token && !loading;

  // Si no hay token en la URL, redirigir al flujo de "olvidé contraseña"
  useEffect(() => {
    if (!token) {
      const t = setTimeout(() => navigate("/forgot-password", { replace: true }), 1500);
      return () => clearTimeout(t);
    }
  }, [token, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "No se pudo restablecer la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="px-6 sm:px-10 py-6">
        <Link
          to="/login"
          data-testid="reset-back-link"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" weight="bold" /> Volver al acceso
        </Link>
      </div>

      <div className="flex-1 flex items-start sm:items-center justify-center px-6 pb-16">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center gap-2 mb-10">
            <LogoWordmark />
          </div>

          {!token ? (
            <div className="text-center text-sm text-zinc-600" data-testid="reset-missing-token">
              Enlace no válido. Redirigiendo...
            </div>
          ) : done ? (
            <div data-testid="reset-done-state" className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-emerald-600" weight="fill" />
              </div>
              <h1 className="font-display font-black text-xl tracking-tight text-zinc-900">
                ¡Contraseña actualizada!
              </h1>
              <p className="text-sm text-zinc-600 mt-3">
                Ya puedes acceder con tu nueva contraseña.
              </p>
              <Link
                to="/login"
                data-testid="reset-go-login"
                className="btn-brand w-full rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 mt-6"
              >
                Iniciar sesión <ArrowRight className="w-4 h-4" weight="bold" />
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-zinc-900">
                  Nueva contraseña
                </h1>
                <p className="text-xs text-zinc-500 mt-1.5">
                  Crea una contraseña fácil de recordar — usa una frase.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
                <div>
                  <label htmlFor="pwd" className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-zinc-600">
                    Contraseña
                  </label>
                  <div className="relative">
                    <input
                      id="pwd"
                      type={showPwd ? "text" : "password"}
                      autoComplete="new-password"
                      autoFocus
                      placeholder="Ej. El sol de los 40"
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      required
                      data-testid="reset-pwd-input"
                      className="!pr-10 !text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      data-testid="reset-toggle-visibility"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900 p-1 rounded"
                      aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showPwd ? <EyeSlash className="w-4 h-4" weight="bold" /> : <Eye className="w-4 h-4" weight="bold" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="pwd2" className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-zinc-600">
                    Repite la contraseña
                  </label>
                  <input
                    id="pwd2"
                    type={showPwd ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={pwd2}
                    onChange={(e) => setPwd2(e.target.value)}
                    required
                    data-testid="reset-pwd2-input"
                    className="!text-sm"
                  />
                  {pwd2.length > 0 && !matches && (
                    <div className="text-[11px] text-rose-600 mt-1" data-testid="reset-mismatch">
                      Las contraseñas no coinciden.
                    </div>
                  )}
                </div>

                {/* Checklist de la política */}
                <div className="rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2.5">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500 mb-2 flex items-center gap-1">
                    <Sparkle className="w-3 h-3" weight="fill" /> Requisitos
                  </div>
                  <ul className="space-y-1">
                    {checks.map((c) => (
                      <li
                        key={c.id}
                        data-testid={`reset-rule-${c.id}`}
                        className={`flex items-start gap-1.5 text-[12px] ${c.ok ? "text-emerald-700" : "text-zinc-500"}`}
                      >
                        {c.ok ? (
                          <CheckCircle className="w-3.5 h-3.5 mt-[2px] flex-shrink-0" weight="fill" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 mt-[2px] flex-shrink-0 text-zinc-300" weight="fill" />
                        )}
                        {c.label}
                      </li>
                    ))}
                  </ul>
                </div>

                {err && (
                  <div
                    className="text-xs px-3 py-2 rounded-md bg-rose-50 text-rose-700 border border-rose-200"
                    data-testid="reset-error"
                  >
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="reset-submit-btn"
                  className="btn-brand w-full rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? "Guardando..." : (
                    <>Restablecer contraseña <ArrowRight className="w-4 h-4" weight="bold" /></>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
